import { daysFromToday, fmtDate } from '@/lib/dates';
import type { BadgeTone } from '@/components';

/**
 * Generic deadline-status math for any date-driven workflow, keyed off a due
 * date vs today. Shared by submittals (review-due) and RFIs (response-due) — and
 * any future domain with a deadline — for list-cell coloring, detail status
 * lines, and reminder-email phrasing.
 *
 * Thresholds:
 *   • overdue  — daysUntil < 0  → danger ("Overdue by N day(s)")
 *   • due-soon — 0 ≤ daysUntil ≤ 3 → warning ("Due today" / "Due in N day(s)")
 *   • on-track — daysUntil > 3  → neutral ("On track — due {date}")
 *   • no due date → neutral ("No due date")
 */
export interface DueStatus {
  daysUntil: number | null;
  tone: BadgeTone;
  label: string;
}

export function dueStatus(iso: string | null | undefined): DueStatus {
  const daysUntil = daysFromToday(iso);

  if (daysUntil == null) {
    return { daysUntil: null, tone: 'neutral', label: 'No due date' };
  }
  if (daysUntil < 0) {
    const n = Math.abs(daysUntil);
    return { daysUntil, tone: 'danger', label: `Overdue by ${n} day${n === 1 ? '' : 's'}` };
  }
  if (daysUntil <= 3) {
    const label = daysUntil === 0 ? 'Due today' : `Due in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`;
    return { daysUntil, tone: 'warning', label };
  }
  return { daysUntil, tone: 'neutral', label: `On track — due ${fmtDate(iso)}` };
}
